/**
 * ZEN ERP — Fatura Editörü Modal
 * Siparişten fatura oluşturmadan önce kalemleri ve tutarları düzenlemeye izin verir.
 */
import React, { useState, useContext, useEffect, useMemo } from "react";
import { X, Plus, Trash2, FileText, Check } from "lucide-react";
import { toast } from "sonner";
import { UICtx } from "../appContexts";
import api from "../api";
import { fmt, ono, convertPrice } from "../appUtils";

const DELIVERY_TERMS = ["FOB", "CIF", "EXW", "DAP", "DDP", "FCA", "CPT", "CIP"];

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const normCur = (c) => { const u = String(c || "TL").toUpperCase(); return u === "TRY" ? "TL" : u; };

// O3: KDV kalem bazlıdır. Eski sürüm tek oranı TÜM matraha uyguluyordu; %20
// mekanizma ile %10 perde aynı faturada yanlış KDV üretiyordu.
function calcTotals(items, discountPercent, fallbackVatRates) {
  const subtotal = items.reduce((s, it) => s + (Number(it.unitPrice || 0) * Number(it.qty || 1)), 0);
  const factor = 1 - (Number(discountPercent) || 0) / 100;
  const discounted = subtotal * factor;
  const fallbackRate = Number((fallbackVatRates || [])[0] ?? 20);
  const groups = new Map();
  items.forEach((it) => {
    const rate = Number(it.kdvRate ?? fallbackRate);
    const net = (Number(it.unitPrice || 0) * Number(it.qty || 1)) * factor;
    groups.set(rate, (groups.get(rate) || 0) + net);
  });
  const vatAmounts = [...groups.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([rate, net]) => ({ rate, net: r2(net), kdv: r2(net * rate / 100) }));
  const vatAmount = r2(vatAmounts.reduce((s, v) => s + v.kdv, 0));
  const total = r2(discounted + vatAmount);
  return { subtotal, discounted, vatAmount, vatAmounts, total };
}

export function InvoiceEditorModal({ order, onClose, onSaved }) {
  const { tr } = useContext(UICtx);
  const o = order;
  const oCur = normCur(o.currency || "TL");
  // Fatura, siparişin para biriminde kesilir. Karışık para birimli siparişlerde
  // (ör. EUR sipariş + TL mekanizma) kalem tutarı çevrilmeden yazılırsa 675 TL,
  // 675 EUR olarak faturalanıyordu. Kur siparişte yoksa /rates'ten alınır.
  const fxNeeded = (o.items || []).some((it) => (it.currency ? normCur(it.currency) : oCur) !== oCur);
  const [fxRates, setFxRates] = useState(o.rates || null);
  const fxReady = !fxNeeded || Number(fxRates?.eurToTl) > 0;
  useEffect(() => {
    if (!fxNeeded || fxReady) return;
    let alive = true;
    api.get("/rates")
      .then(({ data }) => { if (alive) setFxRates(data || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [fxNeeded, fxReady]);

  const [customer, setCustomer] = useState(null);
  useEffect(() => {
    api.get("/customers").then(({ data }) => setCustomer((data || []).find(c => c.id === o.customerId) || null)).catch(() => {});
  }, [o.customerId]);

  // Pre-fill from order — tutarlar fatura para birimine çevrilir
  const convItems = useMemo(() => (o.items || []).map(item => {
    const from = item.currency ? normCur(item.currency) : oCur;
    const conv = (v) => (fxNeeded && fxReady && from !== oCur
      ? r2(convertPrice(Number(v) || 0, from, oCur, fxRates))
      : (Number(v) || 0));
    return {
      description: item.product || "",
      qty: item.qty || 1,
      unit: item.unit || "adet",
      unitPrice: conv(item.unitPrice),
      lineTotal: conv(item.lineTotal),
      // KDV oranı sipariş kaleminden gelir (perde %10, mekanizma %20)
      kdvRate: Number(item.kdvRate ?? 20),
      gtipCode: "",
    };
  }), [o.id, fxRates, fxReady, fxNeeded, oCur]); // eslint-disable-line react-hooks/exhaustive-deps

  const EMPTY_ROW = { description: "", qty: 1, unit: "adet", unitPrice: 0, kdvRate: 20, gtipCode: "" };
  // Kullanıcı düzenleyene kadar kalemler siparişten türetilir; kur geç gelirse
  // ekran kendiliğinden doğru (çevrilmiş) tutara geçer.
  const [editedItems, setEditedItems] = useState(null);
  const items = editedItems ?? (convItems.length ? convItems : [EMPTY_ROW]);
  const setItems = (next) => setEditedItems((prev) => {
    const base = prev ?? (convItems.length ? convItems : [EMPTY_ROW]);
    return typeof next === "function" ? next(base) : next;
  });

  const [discountPercent, setDiscountPercent] = useState(o.discountPercent || 0);
  const orderVatRates = [...new Set((o.items || []).map(it => Number(it.kdvRate ?? 20)))].sort((a, b) => a - b);
  const [vatRates, setVatRates] = useState(
    (o.vatBreakdown || []).map(v => v.rate).length
      ? (o.vatBreakdown || []).map(v => v.rate)
      : (orderVatRates.length ? orderVatRates : [20])
  );
  const [invoiceCategory, setInvoiceCategory] = useState("e_fatura");
  const [docNo, setDocNo] = useState("");
  const [notes, setNotes] = useState(o.notes || "");
  const [saving, setSaving] = useState(false);
  const [isExport, setIsExport] = useState(!!o.vatExempt);
  const [vatExemptNote, setVatExemptNote] = useState(o.vatExemptNote || "3065 sayılı KDV Kanunu Madde 11/1-a kapsamında KDV'den istisnadır");
  const [exportCountry, setExportCountry] = useState("");
  const [customsDeclarationNo, setCustomsDeclarationNo] = useState("");
  const [carrierName, setCarrierName] = useState("");
  const [gtipCode, setGtipCode] = useState("");
  const [deliveryTerms, setDeliveryTerms] = useState("FOB");
  const [paymentTerms, setPaymentTerms] = useState("");

  useEffect(() => {
    if (customer?.vatExemptNote && !o.vatExemptNote) setVatExemptNote(customer.vatExemptNote);
  }, [customer]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = calcTotals(
    isExport ? items.map(it => ({ ...it, kdvRate: 0 })) : items,
    discountPercent,
    isExport ? [0] : vatRates
  );

  const updateItem = (idx, field, val) => {
    const newItems = [...items];
    newItems[idx] = { ...newItems[idx], [field]: val };
    setItems(newItems);
  };

  const addItem = () => setItems([...items, { ...EMPTY_ROW }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const save = async () => {
    if (!fxReady) { toast.error("Kur bilgisi yüklenemedi — fatura para birimi çevrilemiyor"); return; }
    if (!items.some(it => it.description.trim())) { toast.error("En az bir kalem açıklaması girin"); return; }
    setSaving(true);
    try {
      const invoiceItems = items.map(it => ({
        description: it.description,
        qty: Number(it.qty) || 1,
        unit: it.unit || "adet",
        unitPrice: Number(it.unitPrice) || 0,
        lineTotal: (Number(it.unitPrice) || 0) * (Number(it.qty) || 1),
        kdvRate: Number(it.kdvRate ?? 20),
        ...(isExport ? { gtipCode: it.gtipCode || "" } : {}),
      }));
      const body = {
        type: "satis",
        invoiceCategory,
        orderId: o.id,
        customerId: o.customerId,
        customerName: o.customerName,
        currency: o.currency || "TRY",
        issueDate: new Date().toISOString().slice(0, 10),
        items: invoiceItems,
        subtotal: totals.subtotal,
        discountPercent: Number(discountPercent) || 0,
        vatBreakdown: totals.vatAmounts,
        vatAmount: totals.vatAmount,
        total: totals.total,
        notes: `${ono(o)} nolu siparişe ait fatura${notes ? " — " + notes : ""}`,
        docNo: docNo.trim() || undefined,
        ...(isExport ? {
          vatExempt: true,
          vatExemptNote,
          exportCountry, customsDeclarationNo, carrierName, gtipCode,
          deliveryTerms, paymentTerms,
        } : {}),
      };
      const { data } = await api.post("/invoices", body);
      toast.success("Fatura oluşturuldu");
      onSaved && onSaved(data);
      onClose();
    } catch (e) {
      toast.error("Fatura kaydedilemedi");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-5 overflow-y-auto" onClick={onClose} data-testid="invoice-editor-overlay">
      <div className="zen-panel w-full max-w-3xl zen-anim-up" onClick={(e) => e.stopPropagation()} data-testid="invoice-editor-modal">
        {/* Başlık */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-[#22304A] sticky top-0 bg-white dark:bg-[#131B2E] z-10">
          <div>
            <h3 className="text-base font-bold flex items-center gap-2">
              <FileText className="w-4 h-4 text-orange-500" />
              Fatura Oluştur
            </h3>
            <div className="text-[11px] text-slate-500 mt-0.5">
              <span className="zen-mono font-bold text-[#E5322A]">{ono(o)}</span> · {o.customerName}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-[#22304A] transition-colors" data-testid="invoice-editor-close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Fatura tipi + no */}
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-slate-500 block mb-1">Fatura Türü</label>
              <select
                data-testid="invoice-category-select"
                value={invoiceCategory}
                onChange={(e) => setInvoiceCategory(e.target.value)}
                className="w-full rounded-lg border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#0F1626] px-3 py-2 text-sm outline-none focus:border-orange-500"
              >
                <option value="e_fatura">e-Fatura</option>
                <option value="e_arsiv">e-Arşiv</option>
                <option value="kagit">Kâğıt Fatura</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-500 block mb-1">Fatura No (opsiyonel)</label>
              <input
                data-testid="invoice-docno-input"
                value={docNo}
                onChange={(e) => setDocNo(e.target.value)}
                placeholder="Örn: ZEN-2026-001"
                className="w-full rounded-lg border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#0F1626] px-3 py-2 text-sm outline-none focus:border-orange-500"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-500 block mb-1">KDV Oranı (%)</label>
              <div className="flex gap-1 flex-wrap">
                {[0, 10, 20].map(r => (
                  <button
                    key={r}
                    data-testid={`vat-rate-${r}`}
                    type="button"
                    disabled={isExport}
                    onClick={() => {
                      // Tüm kalemlere uygula (kalem bazlı KDV'nin toplu hâli)
                      setVatRates([r]);
                      setItems(its => its.map(x => ({ ...x, kdvRate: r })));
                    }}
                    className={`px-2.5 py-1.5 rounded-lg text-[11px] font-bold border transition-colors disabled:opacity-40 ${(isExport ? 0 : vatRates[0]) === r ? "bg-orange-500 text-white border-orange-500" : "bg-white dark:bg-[#0F1626] border-slate-200 dark:border-[#22304A] text-slate-600 hover:border-orange-400"}`}
                  >
                    %{r}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {customer?.vatExemptEligible && (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs font-bold text-emerald-500">İhracat Faturası (KDV %0)</span>
                  <p className="text-[10px] text-slate-400">Bu cari için istisna seçeneği açık.</p>
                </div>
                <button
                  type="button"
                  data-testid="invoice-export-toggle"
                  onClick={() => setIsExport(v => !v)}
                  className={`shrink-0 w-11 h-6 rounded-full transition-colors relative ${isExport ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-600"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${isExport ? "translate-x-5" : ""}`} />
                </button>
              </div>
              {isExport && (
                <div className="mt-3 space-y-3">
                  <textarea
                    data-testid="invoice-export-vat-note"
                    rows={2}
                    className="w-full rounded-lg border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#0F1626] px-3 py-2 text-xs outline-none focus:border-emerald-500"
                    value={vatExemptNote}
                    onChange={(e) => setVatExemptNote(e.target.value)}
                  />
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                      <label className="text-[11px] font-semibold text-slate-500 block mb-1">Hedef Ülke</label>
                      <input data-testid="invoice-export-country" value={exportCountry} onChange={(e) => setExportCountry(e.target.value)}
                        className="w-full rounded-lg border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#0F1626] px-3 py-2 text-sm outline-none focus:border-emerald-500" />
                    </div>
                    <div>
                      <label className="text-[11px] font-semibold text-slate-500 block mb-1">Gümrük Beyanname No</label>
                      <input data-testid="invoice-customs-declaration" value={customsDeclarationNo} onChange={(e) => setCustomsDeclarationNo(e.target.value)}
                        className="w-full rounded-lg border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#0F1626] px-3 py-2 text-sm outline-none focus:border-emerald-500" />
                    </div>
                    <div>
                      <label className="text-[11px] font-semibold text-slate-500 block mb-1">Nakliyeci</label>
                      <input data-testid="invoice-carrier-name" value={carrierName} onChange={(e) => setCarrierName(e.target.value)}
                        className="w-full rounded-lg border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#0F1626] px-3 py-2 text-sm outline-none focus:border-emerald-500" />
                    </div>
                    <div>
                      <label className="text-[11px] font-semibold text-slate-500 block mb-1">Fatura Geneli GTİP (opsiyonel)</label>
                      <input data-testid="invoice-gtip-code" value={gtipCode} onChange={(e) => setGtipCode(e.target.value)} placeholder="Kalem bazlı GTİP tabloda"
                        className="w-full rounded-lg border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#0F1626] px-3 py-2 text-sm outline-none focus:border-emerald-500" />
                    </div>
                    <div>
                      <label className="text-[11px] font-semibold text-slate-500 block mb-1">Teslim Şekli (Incoterms)</label>
                      <select data-testid="invoice-delivery-terms" value={deliveryTerms} onChange={(e) => setDeliveryTerms(e.target.value)}
                        className="w-full rounded-lg border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#0F1626] px-3 py-2 text-sm outline-none focus:border-emerald-500">
                        {DELIVERY_TERMS.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[11px] font-semibold text-slate-500 block mb-1">Ödeme Şekli</label>
                      <input data-testid="invoice-payment-terms" value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} placeholder="Örn: 30 gün peşin havale"
                        className="w-full rounded-lg border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#0F1626] px-3 py-2 text-sm outline-none focus:border-emerald-500" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Kalemler */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Fatura Kalemleri</div>
              <button type="button" data-testid="invoice-add-item" onClick={addItem} className="flex items-center gap-1 text-[11px] text-orange-500 hover:text-orange-600 font-semibold">
                <Plus className="w-3.5 h-3.5" /> Kalem Ekle
              </button>
            </div>
            {!fxReady && (
              <div data-testid="invoice-fx-wait" className="mb-2 text-[10px] text-amber-600 dark:text-amber-400">
                Kalem para birimi siparişten farklı — kur yükleniyor, tutarlar çevriliyor…
              </div>
            )}
            <div className="zen-panel !p-0 overflow-hidden">
              <table className="w-full text-[12px]">
                <thead className="bg-slate-50 dark:bg-[#0F1626]">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] font-bold uppercase text-slate-500 w-[34%]">Açıklama</th>
                    <th className="px-3 py-2 text-center text-[10px] font-bold uppercase text-slate-500 w-[9%]">Miktar</th>
                    <th className="px-3 py-2 text-center text-[10px] font-bold uppercase text-slate-500 w-[9%]">Birim</th>
                    <th className="px-3 py-2 text-right text-[10px] font-bold uppercase text-slate-500 w-[18%]">Birim Fiyat</th>
                    <th className="px-3 py-2 text-center text-[10px] font-bold uppercase text-slate-500 w-[9%]">KDV</th>
                    <th className="px-3 py-2 text-right text-[10px] font-bold uppercase text-slate-500 w-[14%]">Tutar</th>
                    {isExport && <th className="px-3 py-2 text-center text-[10px] font-bold uppercase text-slate-500 w-[15%]">GTİP</th>}
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-[#22304A]">
                  {items.map((item, idx) => {
                    const lineTotal = (Number(item.unitPrice) || 0) * (Number(item.qty) || 1);
                    return (
                      <tr key={idx}>
                        <td className="px-3 py-1.5">
                          <input
                            data-testid={`item-desc-${idx}`}
                            value={item.description}
                            onChange={(e) => updateItem(idx, "description", e.target.value)}
                            placeholder="Ürün / Açıklama"
                            className="w-full bg-transparent outline-none placeholder-slate-400 dark:placeholder-slate-600"
                          />
                        </td>
                        <td className="px-3 py-1.5">
                          <input
                            data-testid={`item-qty-${idx}`}
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.qty}
                            onChange={(e) => updateItem(idx, "qty", e.target.value)}
                            className="w-full bg-transparent outline-none text-center zen-mono"
                          />
                        </td>
                        <td className="px-3 py-1.5">
                          <input
                            data-testid={`item-unit-${idx}`}
                            value={item.unit}
                            onChange={(e) => updateItem(idx, "unit", e.target.value)}
                            className="w-full bg-transparent outline-none text-center text-slate-500"
                          />
                        </td>
                        <td className="px-3 py-1.5">
                          <input
                            data-testid={`item-price-${idx}`}
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unitPrice}
                            onChange={(e) => updateItem(idx, "unitPrice", e.target.value)}
                            className="w-full bg-transparent outline-none text-right zen-mono"
                          />
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          <select
                            data-testid={`item-kdv-${idx}`}
                            value={Number(item.kdvRate ?? 20)}
                            disabled={isExport}
                            onChange={(e) => updateItem(idx, "kdvRate", Number(e.target.value))}
                            className="bg-transparent outline-none text-center zen-mono disabled:opacity-40">
                            {[0, 10, 20].includes(Number(item.kdvRate ?? 20)) ? null : <option value={Number(item.kdvRate ?? 20)}>{item.kdvRate}</option>}
                            <option value={0}>%0</option>
                            <option value={10}>%10</option>
                            <option value={20}>%20</option>
                          </select>
                        </td>
                        <td className="px-3 py-1.5 text-right zen-mono font-semibold">
                          {fmt(lineTotal, o.currency)}
                        </td>
                        {isExport && (
                          <td className="px-3 py-1.5">
                            <input
                              data-testid={`item-gtip-${idx}`}
                              value={item.gtipCode || ""}
                              onChange={(e) => updateItem(idx, "gtipCode", e.target.value)}
                              placeholder="0000.00.00.00.00"
                              className="w-full bg-transparent outline-none text-center zen-mono text-[11px]"
                            />
                          </td>
                        )}
                        <td className="px-2 py-1.5">
                          {items.length > 1 && (
                            <button type="button" data-testid={`item-remove-${idx}`} onClick={() => removeItem(idx)} className="p-0.5 text-rose-400 hover:text-rose-600 transition-colors">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* İskonto + Özet */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] font-semibold text-slate-500 block mb-1">İskonto (%)</label>
              <input
                data-testid="invoice-discount"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={discountPercent}
                onChange={(e) => setDiscountPercent(e.target.value)}
                className="w-full rounded-lg border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#0F1626] px-3 py-2 text-sm zen-mono outline-none focus:border-orange-500"
              />
              <label className="text-[11px] font-semibold text-slate-500 block mb-1 mt-3">Notlar</label>
              <textarea
                data-testid="invoice-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Fatura notu..."
                className="w-full rounded-lg border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#0F1626] px-3 py-2 text-sm outline-none focus:border-orange-500 resize-none"
              />
            </div>
            <div className="zen-panel p-4 space-y-2 text-[12px]">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Fatura Özeti</div>
              <div className="flex justify-between"><span className="text-slate-500">Ara Toplam</span><span className="zen-mono">{fmt(totals.subtotal, o.currency)}</span></div>
              {discountPercent > 0 && (
                <div className="flex justify-between text-rose-500"><span>İskonto %{discountPercent}</span><span className="zen-mono">-{fmt(totals.subtotal - totals.discounted, o.currency)}</span></div>
              )}
              {totals.vatAmounts.map((v, i) => (
                <div key={i} className="flex justify-between text-slate-400 text-[11px]"><span>KDV %{v.rate}</span><span className="zen-mono">{fmt(v.kdv, o.currency)}</span></div>
              ))}
              <div className="flex justify-between font-bold text-base pt-2 border-t border-slate-200 dark:border-[#22304A]">
                <span>Genel Toplam</span>
                <span className="zen-mono text-orange-500">{fmt(totals.total, o.currency)}</span>
              </div>
              <div className="flex justify-between text-[11px] text-amber-500 pt-1">
                <span>Cari KDV Etkisi</span>
                <span className="zen-mono font-bold">+{fmt(totals.vatAmount, o.currency)}</span>
              </div>
              <div className="text-[10px] text-slate-400 bg-amber-50 dark:bg-amber-500/10 rounded-md p-2 mt-1">
                Sipariş onayında <span className="font-bold">{fmt(totals.subtotal, o.currency)}</span> cari borç işlendi. Bu fatura kesilince sadece KDV tutarı eklenecektir.
              </div>
            </div>
          </div>
        </div>

        {/* Kaydet */}
        <div className="px-5 py-4 border-t border-slate-200 dark:border-[#22304A] flex gap-2 justify-end">
          <button onClick={onClose} className="zen-btn-ghost !text-xs" data-testid="invoice-editor-cancel">Vazgeç</button>
          <button
            data-testid="invoice-editor-save"
            disabled={saving}
            onClick={save}
            className="zen-btn-primary !text-xs flex items-center gap-1.5 disabled:opacity-60"
          >
            <Check className="w-3.5 h-3.5" /> {saving ? "Kaydediliyor..." : "Fatura Oluştur"}
          </button>
        </div>
      </div>
    </div>
  );
}
